/** @file Weights panel body: the sections for the resolved mesh, or a hint when no mesh is selected. */
import type { CommandCall, Doc } from '@pose2d/format/types';
import type { Translate } from '@pose2d/i18n/types';
import type { SelectionSlice } from '../../stores/types';
import type { EditMeshOptions } from '../../tools/editMesh/editMeshStore';
import type { MeshTarget } from '../../tools/editMesh/meshTarget';
import { vertexSelection } from '../../tools/editMesh/vertexSelection';
import type { BrushState } from '../../tools/paintWeights/brushStore';
import { BindingSection } from './BindingSection';
import { BrushSection } from './BrushSection';
import { InfluenceSection } from './InfluenceSection';
import { MeshSection } from './MeshSection';
import { weightsView } from './weightsView';
import './weights.css';

/** Everything the body renders from; WeightsPanel reads it from the stores. */
export interface WeightsBodyProps {
  doc: Doc;
  target: MeshTarget | null;
  selection: SelectionSlice;
  brush: BrushState;
  options: EditMeshOptions;
  animate: boolean;
  /** Translated failure of the last commit, or null. */
  failure: string | null;
  t: Translate;
  commit: (calls: readonly CommandCall[]) => void;
}

/** Mesh, binding, influences and brush sections of `target`; `data-testid="panel-weights"` for e2e. */
export function WeightsBody(p: WeightsBodyProps) {
  if (p.target === null) return <div className="weights-panel weights-muted" data-testid="panel-weights">{p.t('panels.weights.noMesh')}</div>;
  const selected = vertexSelection.of(p.selection, p.target.attachment, p.target.mesh.uvs.length / 2);
  const view = weightsView(p.doc, p.target, selected, p.brush.bone);
  const shared = { view, doc: p.doc, t: p.t, commit: p.commit };
  return (
    <div className="weights-panel" data-testid="panel-weights">
      <BindingSection {...shared} animate={p.animate} />
      <MeshSection {...shared} options={p.options} animate={p.animate} />
      <InfluenceSection {...shared} autoNormalize={p.brush.autoNormalize} addValue={p.brush.value} animate={p.animate} />
      <BrushSection view={view} t={p.t} brush={p.brush} />
      {p.failure === null ? null : <p role="alert" className="weights-error">{p.failure}</p>}
    </div>
  );
}
